/**
 * AppSettingsPage — the "App settings" surface (T12.2 / ⚙️).
 *
 * Device/region config that runs the app. Carries the 🛠️ neutral band (NOT 🧭 —
 * these don't steer suggestions). Rehomes:
 *   - Home location    → LocationCard (GPS toggle, home postal code / address).
 *                        The household location still powers store discovery, but
 *                        the store *list* + discovery checklist now live on the
 *                        Store preferences surface (T12.2 step 9), not here.
 *   - Notifications    → deal alerts / restock reminders (device-local prefs)
 *   - Appearance & sync → theme (real dark mode) / offline status / last synced
 *
 * T12.2 step 10 — the device-local controls are now real:
 *   10a  Theme is wired to the ThemeProvider (System / Light / Dark, persisted,
 *        resolves System via the OS; the dark palette themes every screen).
 *   10b  Offline is an honest read-only status row — offline-first is always on
 *        (not a toggle) — plus a real "Last synced" time.
 *   10c  Notification toggles persist a device-local preference and re-read on
 *        reload. Delivery (push) does NOT exist yet — a quiet "coming soon" note
 *        keeps the toggle honest about not yet firing.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useCurrentHousehold,
  useHouseholds,
  useMe,
} from '@shopista/shared/api/hooks';
import {
  componentValue,
  deriveEnv,
  freshnessLabel,
  metaLine,
  serverValue,
  supportLine,
  versionString,
  SERVER_UNREACHABLE,
} from '../../lib/appVersion';
import { useFreshness } from '../../lib/useFreshness';
import { useVersionDisplay } from '../../lib/useVersionDisplay';
import { useServerVersion } from '../../lib/useServerVersion';
import { useNativeAppUpdate } from '../../lib/useNativeAppUpdate';
import { Card } from '../../components';
import { routes } from '../../routes/paths';
import { useTheme } from '../../theme/ThemeProvider';
import {
  DEFAULT_NOTIFICATION_PREFS,
  formatLastSynced,
  persistNotificationPrefs,
  readLastSyncedAt,
  readNotificationPrefs,
  type NotificationPrefs,
} from '../../stores/deviceSettings';
import { SettingsLayover } from './SettingsLayover';
import {
  LocationCard,
  sectionHeaderStyle,
  setgroupCardPaddedStyle,
} from './cards';
import { SwitchRow, Choice, hintStyle, type ScaleOption } from './prefControls';

const THEME_OPTIONS: ScaleOption[] = [
  { value: 'system', label: 'System' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
];

// T29 — the API URL is baked at build time; reused to derive the env label.
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3001';

/** Title-case the platform for the everyone-view (`web` → `Web`, `android` → `Android`). */
function platformLabel(platform: string): string {
  if (platform.length === 0) return platform;
  return platform.charAt(0).toUpperCase() + platform.slice(1);
}

// A quiet refresh nudge shown when a newer build is deployed than the one this
// tab is running (D10). Users see only this — never a build number.
const refreshNudgeStyle = {
  display: 'block',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-muted)',
  marginTop: 'var(--space-1)',
} as const;

const refreshLinkStyle = {
  background: 'none',
  border: 'none',
  padding: 0,
  margin: 0,
  font: 'inherit',
  color: 'var(--color-accent, var(--color-text-primary))',
  textDecoration: 'underline',
  cursor: 'pointer',
} as const;

// T29.2 — the detailed view is a 2-column grid (label | value). The label is its
// OWN column (`max-content`), never space-padding, so a long value wraps inside
// its own column and the label column never shears (D3); the value cell's
// `minWidth: 0` + `overflowWrap` lets the column shrink and break a long value.
const versionGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'max-content 1fr',
  columnGap: 'var(--space-3)',
  rowGap: 'var(--space-1)',
  fontSize: 'var(--font-size-xs)',
  fontFamily: 'var(--font-family-mono, monospace)',
  alignItems: 'baseline',
  paddingTop: 'var(--space-2)',
} as const;

const versionGridLabelStyle = {
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--color-text-secondary, var(--color-text-muted))',
  whiteSpace: 'nowrap',
} as const;

const versionGridValueStyle = {
  color: 'var(--color-text-primary)',
  minWidth: 0,
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
} as const;

const versionGridMetaStyle = {
  color: 'var(--color-text-muted)',
  minWidth: 0,
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
} as const;

const fieldRowStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'var(--space-3)',
  padding: 'var(--space-2) 0',
} as const;

const fieldLabelStyle = {
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--color-text-primary)',
} as const;

const fieldSubStyle = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-muted)',
  marginTop: 'var(--space-1)',
} as const;

const fieldValueStyle = {
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-muted)',
} as const;

// T27.2.10.2 — the Legal & privacy links. These point at the PUBLIC legal routes
// (outside AuthGuard) so the same pages a Play reviewer reads are reachable from
// inside the app. A simple row of underlined links, token-styled.
const legalRowStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-2)',
  paddingTop: 'var(--space-1)',
} as const;

const legalLinkStyle = {
  display: 'flex',
  alignItems: 'center',
  minHeight: '2.75rem',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-accent, var(--color-text-primary))',
  textDecoration: 'none',
} as const;

// A small "coming soon" pill that keeps the notification toggles honest — the
// preference persists, but nothing delivers a push yet (separate backend Task).
const comingSoonPillStyle = {
  display: 'inline-block',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--color-text-muted)',
  backgroundColor: 'var(--color-bg-muted)',
  borderRadius: 'var(--radius-pill)',
  padding: '0.0625rem var(--space-2)',
  marginLeft: 'var(--space-2)',
} as const;

export function AppSettingsPage(): JSX.Element {
  const householdQuery = useCurrentHousehold();
  const householdsQuery = useHouseholds();
  const household = householdQuery.data;
  const currentMembership = (householdsQuery.data ?? []).find((h) => h.is_current);
  const isManager = currentMembership?.role === 'manager';

  // 10a — theme is a real device-local preference via ThemeProvider.
  const { choice: theme, setChoice: setTheme } = useTheme();

  // 10c — notification prefs persist device-local + re-read on reload.
  const [notificationPrefs, setNotificationPrefs] = useState<NotificationPrefs>(
    () => DEFAULT_NOTIFICATION_PREFS,
  );
  // Read once on mount (deferred so the initial render is SSR-safe + cheap).
  useEffect(() => {
    setNotificationPrefs(readNotificationPrefs());
  }, []);

  const toggleNotification = (key: keyof NotificationPrefs): void => {
    setNotificationPrefs((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      persistNotificationPrefs(next);
      return next;
    });
  };

  // 10b — honest "Last synced" sourced from the device-local timestamp the shell
  // records on each completed pull. Read on mount (it only updates between page
  // loads from this surface's perspective).
  const [lastSynced] = useState<string | null>(() => readLastSyncedAt());

  // T29 / T29.2 — version surfaces. `isAdmin` gates the detailed grid so it works
  // on production where the developer actually debugs (D12). Everyone sees the
  // support line (`<platform> · 1.0.<build> · <channel> · <freshness>`) — the
  // sayable identifier that replaces the old "Version 1.0".
  const isAdmin = useMe().data?.isAdmin === true;
  const freshness = useFreshness();
  const serverVersion = useServerVersion();
  // T29.2.4 — ONE decision site (D2). `useVersionDisplay` keys live-vs-frozen on
  // `import.meta.env.DEV` alone (D1) and returns the running build to show, its
  // channel, the optional native-shell identity, and the precomputed drift. This
  // surface holds NO mode inference of its own.
  const version = useVersionDisplay();
  // The running web bundle's stamp (the Web-row source on web / live, the drift +
  // meta source on native). On native the Mobile row reads `version.native`.
  const webBuild = version.build;
  // `native-shell` is the only kind that renders the Mobile row; `live-web` (incl.
  // local live-reload inside the shell) and `baked-web` render the Web row (D3).
  const isNative = version.kind === 'native-shell';
  const nativeVersion = version.native;
  const showStale = !isNative && freshness.isStale;
  // T27.2.9 D4 — native Mobile-row freshness word ("is a newer build on my Play
  // track?"). Inert off native; the launch/resume Play-Store nudge owns the
  // ACTION — this only feeds the resting word into the Mobile row.
  const nativeUpdate = useNativeAppUpdate();

  const env = deriveEnv(API_BASE_URL);
  // Freshness word. On native (T27.2.9 D4) it answers "newer build on my Play
  // track?" from the update check — `✓ up to date` / `⟳ Update available`, or
  // omitted until a check resolves / when it couldn't run (a non-null sentinel
  // only when `checked`, so freshnessLabel never raises a false signal). On web
  // it's the deploy-freshness word (D6), still '' when /version.json couldn't be
  // read. The native word is display-only — the web-only Reload affordance below
  // stays gated on `!isNative`.
  const freshnessText = isNative
    ? freshnessLabel(nativeUpdate.updateAvailable, nativeUpdate.checked ? 'native' : null)
    : freshnessLabel(freshness.isStale, freshness.latestBuild);

  // Everyone-view support line — platform + channel from the native shell on
  // native, the running bundle on web. On web the live `webBuild` (local) or the
  // baked stamp supplies version + channel.
  const everyoneLine = isNative
    ? supportLine(
        platformLabel(nativeVersion!.platform),
        { semver: nativeVersion!.version, build: nativeVersion!.build },
        __APP_BUILD__.channel,
        freshnessText,
      )
    : supportLine(platformLabel('web'), webBuild, webBuild.channel, freshnessText);

  // Native drift: the installed shell's build differs from the running web
  // bundle's build (dev live-reload). Precomputed by the hook (D5); only the
  // `native-shell` kind can carry it.
  const hasDrift = version.hasDrift;

  return (
    <SettingsLayover
      emoji="⚙️"
      title="App settings"
      backLabel="‹ Menu"
      testId="settings-app"
      band={{
        variant: 'neutral',
        children: (
          <>
            App behaviour and your account — these don&apos;t steer suggestions,
            they run the app.
          </>
        ),
      }}
    >
      {/* F5 (T27.2.5.5) — Appearance (Theme) is surfaced near the TOP of App
          settings. It used to sit at the very bottom, below Units / Home
          location / Notifications, so a user who came here to fix dark mode had
          to scroll past everything to reach the switch. Moved up for
          discoverability — especially while dark mode is being polished. The
          card carries the same Theme + Offline-first + Last synced + the T29
          About/version rows, all unchanged; only its position moved. */}
      <Card
        padding="none"
        shadow="none"
        style={setgroupCardPaddedStyle}
        data-testid="settings-appearance"
      >
        <h3 style={sectionHeaderStyle}>Appearance &amp; sync</h3>
        <div style={fieldRowStyle}>
          <span style={fieldLabelStyle}>Theme</span>
          <Choice
            options={THEME_OPTIONS}
            value={theme}
            onPick={(v) => setTheme(v as typeof theme)}
            testId="settings-appearance-theme"
          />
        </div>

        {/* 10b — offline-first is always on; this is a status row, not a toggle. */}
        <div style={fieldRowStyle} data-testid="settings-appearance-offline">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={fieldLabelStyle}>Offline-first</div>
            <div style={fieldSubStyle}>
              Always on — works without signal, syncs when you&apos;re back.
            </div>
          </div>
          <span style={fieldValueStyle}>On</span>
        </div>

        <div style={fieldRowStyle}>
          <span style={fieldLabelStyle}>Last synced</span>
          <span style={fieldValueStyle} data-testid="settings-appearance-last-synced">
            {formatLastSynced(lastSynced)}
          </span>
        </div>

        {/* T29.2 — About / version. Everyone sees the support line (the sayable
            identifier `<platform> · 1.0.<build> · <channel> · <freshness>`).
            Admin (or the reveal) additionally sees the detailed 2-column grid:
            the app row (Web on web / Mobile on native — Mobile + Web collapse,
            D5), a `⚠ Web` drift row when the bundle is newer than the shell, and
            the Server row with its own build date. The env labels the Server row,
            never the app's build (channel is the app's branch — D4). */}
        {isAdmin ? (
          <div data-testid="settings-about-admin">
            <div style={versionGridStyle}>
              {/* App row — Mobile on native (the installed APK; Mobile + Web
                  collapse to one row), Web on web (the running bundle). The value
                  carries the version · channel · (web-only) freshness; the muted
                  meta cell beneath carries the sha · date. */}
              <span style={versionGridLabelStyle}>{isNative ? 'Mobile' : 'Web'}</span>
              <span style={versionGridValueStyle} data-testid="settings-about-app">
                {isNative
                  ? componentValue(
                      { semver: nativeVersion!.version, build: nativeVersion!.build },
                      __APP_BUILD__.channel,
                      freshnessText,
                    )
                  : componentValue(webBuild, webBuild.channel, freshnessText)}
              </span>
              <span style={versionGridLabelStyle} aria-hidden="true" />
              <span style={versionGridMetaStyle} data-testid="settings-about-app-meta">
                {metaLine(
                  isNative ? __APP_BUILD__.sha : webBuild.sha,
                  isNative ? __APP_BUILD__.date : webBuild.date,
                )}
              </span>

              {/* Drift only — the running web bundle is newer than the installed
                  shell (dev live-reload). Otherwise absent (D5). */}
              {hasDrift && (
                <>
                  <span style={versionGridLabelStyle} data-testid="settings-about-drift">
                    ⚠ Web
                  </span>
                  <span style={versionGridValueStyle}>
                    {`${versionString(__APP_BUILD__)} · ${__APP_BUILD__.channel} — newer than the app`}
                  </span>
                </>
              )}

              {/* Server row — env-led, the reached backend's version + its OWN
                  build date (D7). A quiet em dash when unreachable. */}
              <span style={versionGridLabelStyle}>Server</span>
              {serverVersion ? (
                <>
                  <span style={versionGridValueStyle} data-testid="settings-about-server">
                    {serverValue(env, serverVersion)}
                  </span>
                  <span style={versionGridLabelStyle} aria-hidden="true" />
                  <span style={versionGridMetaStyle} data-testid="settings-about-server-meta">
                    {metaLine(serverVersion.sha, serverVersion.date)}
                  </span>
                </>
              ) : (
                <span style={versionGridValueStyle} data-testid="settings-about-server">
                  {SERVER_UNREACHABLE}
                </span>
              )}
            </div>

            {/* Explicit "am I on the latest?" answer (web only — a bundled native
                build can't be behind a deploy, D5/D6). Reads `✓ up to date` when
                current, nothing when /version.json couldn't be read, and the
                update prompt + Reload when a newer build is deployed. */}
            {!isNative && freshnessText.length > 0 && (
              <span style={refreshNudgeStyle} data-testid="settings-about-freshness">
                {freshnessText}
                {showStale && (
                  <>
                    {' '}—{' '}
                    <button
                      type="button"
                      style={refreshLinkStyle}
                      onClick={() => window.location.reload()}
                    >
                      Reload
                    </button>
                  </>
                )}
              </span>
            )}
          </div>
        ) : (
          <div style={fieldRowStyle} data-testid="settings-about">
            <span style={fieldLabelStyle}>About Shopista</span>
            <span style={{ ...fieldValueStyle, textAlign: 'right' }} data-testid="settings-about-line">
              {everyoneLine}
              {showStale && (
                <span style={refreshNudgeStyle} data-testid="settings-about-stale">
                  🔄 New version available —{' '}
                  <button
                    type="button"
                    style={refreshLinkStyle}
                    onClick={() => window.location.reload()}
                  >
                    Refresh
                  </button>
                </span>
              )}
            </span>
          </div>
        )}
      </Card>

      <LocationCard
        household={household}
        isManager={isManager}
        onDiscoveredStores={() => undefined}
      />

      <Card
        padding="none"
        shadow="none"
        style={setgroupCardPaddedStyle}
        data-testid="settings-notifications"
      >
        <h3 style={sectionHeaderStyle}>Notifications</h3>
        <p style={hintStyle}>
          We keep these gentle — no nagging. We&apos;ll remember your choice;
          delivery is coming soon.
        </p>
        <SwitchRow
          title="Deal alerts"
          desc={
            <>
              When something you buy goes on sale
              <span style={comingSoonPillStyle}>Coming soon</span>
            </>
          }
          on={notificationPrefs.dealAlerts}
          onToggle={() => toggleNotification('dealAlerts')}
          testId="settings-notifications-deals"
        />
        <SwitchRow
          title="Restock reminders"
          desc={
            <>
              When you&apos;re likely running low
              <span style={comingSoonPillStyle}>Coming soon</span>
            </>
          }
          on={notificationPrefs.restockReminders}
          onToggle={() => toggleNotification('restockReminders')}
          testId="settings-notifications-restock"
        />
      </Card>

      {/* T27.2.10.2 — Legal & privacy. Links the public legal pages (Privacy
          Policy + Terms) from inside the app; both routes sit outside AuthGuard
          and render the docs/legal drafts. */}
      <Card
        padding="none"
        shadow="none"
        style={setgroupCardPaddedStyle}
        data-testid="settings-legal"
      >
        <h3 style={sectionHeaderStyle}>Legal &amp; privacy</h3>
        <div style={legalRowStyle}>
          <Link to={routes.privacy} style={legalLinkStyle} data-testid="settings-privacy-link">
            Privacy Policy
          </Link>
          <Link to={routes.terms} style={legalLinkStyle} data-testid="settings-terms-link">
            Terms of Service
          </Link>
          <Link to={routes.deleteAccount} style={legalLinkStyle} data-testid="settings-delete-account-link">
            Delete your account
          </Link>
        </div>
      </Card>
    </SettingsLayover>
  );
}
